import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {{ startX: number, startY: number, startTx: number, startTy: number, img: HTMLElement, media: HTMLElement, pointerId: number } | null} */
  #dragState = null;

  /** Whether the last pointerdown turned into a drag (suppresses the following click) */
  #wasDragging = false;

  /** Zoom scale for each level, indexed from 0 */
  static #ZOOM_SCALES = [1.8, 2.8, 4];

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
    /**
   * Resets all zoom state (class, inline transform, dataset) for a single media container.
   * @param {HTMLElement} item
   */
  #resetItemZoom(item) {
    item.classList.remove('is-zoomed-1', 'is-zoomed-2', 'is-zoomed-3', 'is-dragging');
    item.removeAttribute('data-zoom-level');
    delete item.dataset.translateX;
    delete item.dataset.translateY;
    const img = item.querySelector('.product-media__image');
    if (img instanceof HTMLElement) {
      img.style.removeProperty('transform');
      img.style.removeProperty('transform-origin');
    }
  }

  /**
   * Applies a translate+scale transform to an image element.
   * Using `translate(tx, ty) scale(N)` keeps tx/ty in screen-space pixels,
   * making drag arithmetic trivial (no division by scale needed).
   * @param {HTMLElement} img
   * @param {number} tx - Screen-space X offset in px.
   * @param {number} ty - Screen-space Y offset in px.
   * @param {number} scale - Scale factor.
   */
  #applyZoomTransform(img, tx, ty, scale) {
    img.style.transformOrigin = 'center center';
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  }

  /**
   * Handles click on an image inside the zoom dialog.
   * Cycles through 3 zoom levels anchored to the click point, then closes.
   * @param {number} index
   * @param {PointerEvent} event
   */
  handleImageClick(index, event) {
    // If the pointer-down turned into a drag, swallow this click.
    if (this.#wasDragging) {
      this.#wasDragging = false;
      return;
    }

    const { media } = this.refs;
    const targetMedia = media[index];
    if (!targetMedia) return;

    const currentZoom = parseInt(targetMedia.getAttribute('data-zoom-level') || '0');

    // After max zoom level, close.
    if (currentZoom >= 3) {
      this.close();
      return;
    }

    // Reset every other item.
    for (const item of media) {
      if (item !== targetMedia) this.#resetItemZoom(item);
    }

    const nextZoom = currentZoom + 1;
    const scale = ZoomDialog.#ZOOM_SCALES[nextZoom - 1];

    // Compute translate so the exact click point stays fixed on screen.
    // With transform: translate(tx,ty) scale(N) and transform-origin:center,
    // a point at normalised offset (cx, cy) from the element centre appears at:
    //   screen_pos = element_centre + translate + scale * element_offset
    // To keep it fixed we need:
    //   tx = -(scale - 1) * (clickX - centreX)
    // where clickX and centreX are in screen pixels.
    const rect = targetMedia.getBoundingClientRect();
    const cx = event.clientX - (rect.left + rect.width / 2);
    const cy = event.clientY - (rect.top + rect.height / 2);
    const tx = -(scale - 1) * cx;
    const ty = -(scale - 1) * cy;

    targetMedia.dataset.translateX = String(tx);
    targetMedia.dataset.translateY = String(ty);
    targetMedia.classList.remove('is-zoomed-1', 'is-zoomed-2', 'is-zoomed-3');
    targetMedia.classList.add('is-zoomed-' + nextZoom);
    targetMedia.setAttribute('data-zoom-level', String(nextZoom));

    const img = targetMedia.querySelector('.product-media__image');
    if (img instanceof HTMLElement) this.#applyZoomTransform(img, tx, ty, scale);

    if (nextZoom === 1) this.loadHighResolutionImage(targetMedia);
  }

  /**
   * Starts a drag-to-pan gesture on a zoomed image.
   * @param {number} index
   * @param {PointerEvent} event
   */
  handleImagePointerDown(index, event) {
    const { media } = this.refs;
    const targetMedia = media[index];
    if (!targetMedia) return;

    const zoomLevel = parseInt(targetMedia.getAttribute('data-zoom-level') || '0');
    if (zoomLevel === 0) return; // not zoomed — let the click handler deal with it

    const img = targetMedia.querySelector('.product-media__image');
    if (!(img instanceof HTMLElement)) return;

    this.#wasDragging = false;
    this.#dragState = {
      startX: event.clientX,
      startY: event.clientY,
      startTx: parseFloat(targetMedia.dataset.translateX || '0'),
      startTy: parseFloat(targetMedia.dataset.translateY || '0'),
      img,
      media: targetMedia,
      pointerId: event.pointerId,
    };

    // Capture so pointermove/pointerup keep firing even if the pointer leaves the element.
    targetMedia.setPointerCapture(event.pointerId);
  }

  /**
   * Pans the zoomed image as the pointer moves.
   * @param {number} index
   * @param {PointerEvent} event
   */
  handleImagePointerMove(index, event) {
    if (!this.#dragState) return;

    const { startX, startY, startTx, startTy, img, media } = this.#dragState;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;

    // Only commit to drag mode after a small threshold to avoid eating taps.
    if (!this.#wasDragging && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) {
      this.#wasDragging = true;
      media.classList.add('is-dragging');
    }

    if (!this.#wasDragging) return;

    const newTx = startTx + dx;
    const newTy = startTy + dy;

    media.dataset.translateX = String(newTx);
    media.dataset.translateY = String(newTy);

    const zoomLevel = parseInt(media.getAttribute('data-zoom-level') || '1');
    const scale = ZoomDialog.#ZOOM_SCALES[zoomLevel - 1] ?? 1.8;

    this.#applyZoomTransform(img, newTx, newTy, scale);
  }

  /**
   * Ends a drag-to-pan gesture.
   * @param {number} index
   * @param {PointerEvent} event
   */
  handleImagePointerUp(index, event) {
    if (this.#dragState) {
      this.#dragState.media.classList.remove('is-dragging');
      this.#dragState = null;
    }
    // #wasDragging intentionally stays true until handleImageClick resets it.
  }

  async close() {
    try {
      const { dialog, media } = this.refs;

      if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

   slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    } catch (e) {
      this.closeDialog();
    }
  }
closeDialog() {
    try {
      const { dialog, media } = this.refs;

      this.#dragState = null;
      this.#wasDragging = false;

      if (media) {
        for (const item of media) {
          this.#resetItemZoom(item);
        }
      }

      dialog.close();
      window.dispatchEvent(new DialogCloseEvent());
    } catch (e) {
      this.refs.dialog.close();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
